import React, { ReactNode } from "react";
import { Trans } from "react-i18next";
import { useSubscribe, useSubscriptions } from "../../root/profile/subscription/subscriptionHooks";
import { useViewerQuery } from "../../queries/viewerQuery";
import { useNavigate } from "react-router";
import { SUBSCRIPTION } from "../../root/profile/ProfileRouter.config";
import { TUTORIALDONE } from "../../root/tutorial/steps/Verdict";
import { useEffectOnce } from "react-use";
import useOnboardingCompleted from "hooks/useOnboardingCompleted";
import { LATE_REGISTRATION } from "root/RootRouter.config";
import { useCreateABTest } from "../../queries/users/useCreateABTest";
import { usePaywallTitle } from "./usePaywallTitle";
import { useUserSale } from "queries/users/userSaleQuery";
import { SALE } from "sharedJs__generated/constants";
import InternalAdPaywallBlackFriday from "./InternalAdPaywallBlackFriday";
import InternalAdPaywallDefault from "./InternalAdPaywallDefault";
import { useStoreIds } from "../../root/profile/subscription/subscriptionIds";
import styled from "styled-components";

const BoldText = styled.span`
  font-weight: 700;
`;

type Props = { onClose: VoidFunction };

const InternalAdPaywall: React.FC<Props> = ({ onClose }) => {
  const { subscriptions } = useSubscriptions();
  const { storeIdYear } = useStoreIds();
  const { hintWasSeen, isSubscriber } = useViewerQuery();
  const subscribe = useSubscribe();
  const { userSale } = useUserSale();
  const navigate = useNavigate();
  const [isCloseVisible, setIsCloseVisible] = React.useState(false);
  const onboardingCompleted = useOnboardingCompleted();
  const abtestRegisterBeforeSubscribeCreate = useCreateABTest("abtest_onDemand_registerBeforeSubscribe");
  const { title: translatedTitle, isLoading, isAbTestEnabled } = usePaywallTitle();

  const trialAvailable = subscriptions?.[storeIdYear]?.isTrial;

  useEffectOnce(() => {
    setTimeout(() => setIsCloseVisible(true), 300);
  });

  const handleSubscribe = (e: React.MouseEvent) => {
    if (!trialAvailable && !onboardingCompleted && abtestRegisterBeforeSubscribeCreate()) {
      navigate(LATE_REGISTRATION.url());
    } else if (!hintWasSeen(TUTORIALDONE)) {
      subscribe();
    } else {
      navigate(SUBSCRIPTION.url());
    }
    e.stopPropagation();
  };

  React.useEffect(() => {
    if (isSubscriber || !subscriptions) {
      onClose();
    }
  }, [isSubscriber, subscriptions, onClose]);

  let title: ReactNode | string = (
    <Trans>
      Get more from your <BoldText>study&nbsp;plan!</BoldText>
    </Trans>
  );

  if (isAbTestEnabled && !isLoading && translatedTitle) {
    title = translatedTitle;
  }

  if (userSale?.id === SALE.blackFriday) {
    return (
      <InternalAdPaywallBlackFriday onClose={onClose} isCloseVisible={isCloseVisible} onSubscribe={handleSubscribe} />
    );
  }

  return (
    <InternalAdPaywallDefault
      onClose={onClose}
      isCloseVisible={isCloseVisible}
      onSubscribe={handleSubscribe}
      title={title}
      isAbTestEnabled={isAbTestEnabled}
    />
  );
};

export default InternalAdPaywall;
